// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { showToast } from '../utils/helpers';
import api, { fetchCsrfToken, initializeCsrfProtection, updateAuthToken } from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  // Connected bank accounts (user_bank_accounts). Payment accounts are no
  // longer generated, so this is the source of truth for receiving details.
  const [bankAccounts, setBankAccounts] = useState([]);
  const [userProfileComplete, setUserProfileComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug logging
  const log = useCallback((message, data = null) => {
    if(process.env.NODE_ENV === "dev"){
    console.log(` [Auth] ${message}`, data || '');
    }
  }, []);

  // Clear auth data
  const clearAuthData = useCallback(() => {
    log('Clearing auth data');
    localStorage.removeItem('_t');
    localStorage.removeItem('_rt');
    localStorage.removeItem('userData');
    localStorage.removeItem('accountDetails');
    sessionStorage.clear();
  }, [log]);

  // Refresh the seller's connected bank accounts (user_bank_accounts).
  const refreshBankAccounts = useCallback(async () => {
    try {
      const response = await api.get(API_CONFIG.USER.EXISTING_ACCOUNTS);
      if (response.data?.success) {
        setBankAccounts(response.data.data || []);
        return true;
      }
    } catch (error) {
      log('Error refreshing bank accounts', error);
    }
    return false;
  }, [log]);

  // Receiving details are derived from the connected bank accounts — payment
  // accounts (payment_accounts) are no longer generated.
  const currentAccountDetails = useMemo(() => {
    const bank = bankAccounts.find((a) => Number(a.is_default) === 1) || bankAccounts[0] || null;
    if (!bank) return null;
    return {
      account_number: bank.account_number || null,
      account_name: bank.account_name || null,
      bank_name: bank.bank_name || null,
      bank_code: bank.bank_code || null,
      // No payment accounts exist anymore, so the pre-Paymeo upgrade path is
      // never triggered.
      is_pre_paymeo: 0,
      currency: 'NGN',
      balance: 0,
      ledger_balance: 0
    };
  }, [bankAccounts]);

  // Existing account info for transfer functionality.
  const existingAccount = useMemo(() => {
    const bank = bankAccounts.find((a) => Number(a.is_default) === 1) || bankAccounts[0] || null;
    if (!bank) return null;
    return {
      account_number: bank.account_number || null,
      account_name: bank.account_name || null,
      bank_name: bank.bank_name || null
    };
  }, [bankAccounts]);

  // Unified function to set user data from any source
  const setUserData = useCallback((userData, accountData = null, token = null, refreshToken = null) => {
    log('Setting user data', { 
      userId: userData?.id, 
      hasAccountData: !!accountData,
      hasToken: !!token 
    });

    // Process user data to ensure consistent structure
    const processedUser = { ...userData };
    
    // Set has_transaction_pin based on transaction_pin existence
    if (processedUser.transaction_pin !== undefined && processedUser.transaction_pin != null) {
      processedUser.has_transaction_pin = true;
    }
    
    // Remove sensitive fields
    delete processedUser.password;
    delete processedUser.transaction_pin;
    delete processedUser.reset_code;
    delete processedUser.verification_code;

    // Receiving details now come from the connected bank accounts
    // (user_bank_accounts), fetched via refreshBankAccounts() — payment
    // accounts are no longer generated. Any stale payment-account cache is
    // dropped so the old account_number can't gate the UI.

    // Update state
    setCurrentUser(processedUser);
    setUserProfileComplete(processedUser.company_details_complete || false);

    // Save to localStorage
    localStorage.setItem('userData', JSON.stringify(processedUser));
    if (token) {
      localStorage.setItem('_t', token);
    }
    if (refreshToken) {
      localStorage.setItem('_rt', refreshToken);
    }
    localStorage.removeItem('accountDetails');

    log('User data saved successfully', {
      userId: processedUser.id,
      profileComplete: processedUser?.company_details_complete || false
    });
  }, [log]);

  // Process /isLoggedIn response data
  const processIsLoggedInResponse = useCallback((responseData) => {
    log('Processing /isLoggedIn response', responseData);
    
    // Check different possible response structures
    let userData = null;
    let accountData = null;

    // Structure 1: Direct user and account properties
    if (responseData.user) {
      userData = responseData.user;
      accountData = responseData.account;
    }
    // Structure 2: Nested in data property
    else if (responseData.data?.user) {
      userData = responseData.data.user;
      accountData = responseData.data.account;
    }
    // Structure 3: User data might be at root level with account nested
    else if (responseData.id) {
      userData = responseData;
      accountData = responseData.account;
    }

    // If we found user data but no account data, try to get from localStorage as fallback
    if (userData && !accountData) {
      try {
        const cachedAccount = localStorage.getItem('accountDetails');
        if (cachedAccount) {
          accountData = JSON.parse(cachedAccount);
          log('Using cached account data as fallback');
        }
      } catch (e) {
        log('Error reading cached account data', e);
      }
    }

    return { userData, accountData };
  }, [log]);

  // Initialize auth - Fixed version
// In your AuthContext.jsx - update the initializeAuth function
const initializeAuth = useCallback(async () => {
  try {
    log('Initializing authentication...');
    setLoading(true);
    
    // Try to initialize CSRF (but don't block if it fails)
    try {
      const csrfSuccess = await initializeCsrfProtection();
      if (csrfSuccess) {
        log('CSRF protection initialized');
      } else {
        log('CSRF initialization failed, continuing without it');
      }
    } catch (csrfError) {
      log('CSRF initialization failed, continuing without it', csrfError);
      // Don't throw error, continue without CSRF
    }

    const token = localStorage.getItem('_t');
    const cachedUserData = localStorage.getItem('userData');
    const cachedAccountData = localStorage.getItem('accountDetails');

    log('Storage check:', { 
      hasToken: !!token, 
      hasUserData: !!cachedUserData,
      hasAccountData: !!cachedAccountData
    });

    if (token && cachedUserData) {
      try {
        log('Verifying token with /isLoggedIn endpoint...');
        const response = await api.get(API_CONFIG.AUTH.IS_LOGGED_IN);
        
        log('/isLoggedIn raw response:', response.data);

        if (response.data) {
          const { userData, accountData } = processIsLoggedInResponse(response.data);
          
          if (userData) {
            log('/isLoggedIn processed successfully');
            setUserData(userData, accountData, token);
          } else {
            log('No user data in /isLoggedIn response, using cached data');
            const parsedUser = JSON.parse(cachedUserData);
            const parsedAccount = cachedAccountData ? JSON.parse(cachedAccountData) : null;
            setUserData(parsedUser, parsedAccount, token);
          }
        } else {
          log('Empty /isLoggedIn response, using cached data');
          const parsedUser = JSON.parse(cachedUserData);
          const parsedAccount = cachedAccountData ? JSON.parse(cachedAccountData) : null;
          setUserData(parsedUser, parsedAccount, token);
        }
      } catch (error) {
        log('Error during token verification', error);
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          log('Token invalid, clearing auth data');
          clearAuthData();
        } else {
          log('Network error, using cached data as fallback');
          const parsedUser = JSON.parse(cachedUserData);
          const parsedAccount = cachedAccountData ? JSON.parse(cachedAccountData) : null;
          setUserData(parsedUser, parsedAccount, token);
        }
      }
    } else {
      log('No token or user data found in storage');
      clearAuthData();
    }

    // Payment accounts are no longer generated — connected bank accounts
    // (user_bank_accounts) are the source of truth for receiving details.
    if (localStorage.getItem('_t')) {
      await refreshBankAccounts();
    }
  } catch (error) {
    log('Auth initialization error', error);
    clearAuthData();
  } finally {
    log('Auth initialization complete');
    setLoading(false);
  }
}, [clearAuthData, log, setUserData, processIsLoggedInResponse, refreshBankAccounts]);
  // Login function - REMOVED TOAST MESSAGES
const login = async (username, password) => {
    log('Login attempt started', { username: username.substring(0, 3) + '***' });
    
    try {
        setError(null);
        clearAuthData();
        
        log('Sending login request...');
        const backendURL = process.env.REACT_APP_BACKEND_URL;
    
        const response = await fetch(`${backendURL}/app/api/login`, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include', 
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                user: username,
                pass: password,
            }),
        });
        
        // Try to parse the response body even for error status codes
        let data;
        try {
            data = await response.json();
        } catch (parseError) {
            // If response isn't JSON, use status text
            data = { error: response.statusText || 'Unknown error occurred' };
        }
        
        // Log the full response for debugging
        // console.log('Login response:', { 
        //     status: response.status, 
        //     ok: response.ok,
        //     data 
        // });
        
        // Handle different HTTP status codes
        if (!response.ok) {
            // Check if the API returned a custom error message
            if (data && data.error) {
                throw new Error(data.error);
            } else if (data && data.message) {
                throw new Error(data.message);
            } else {
                // Handle based on status code
                switch (response.status) {
                    case 401:
                        throw new Error('Invalid username or password');
                    case 403:
                        throw new Error('Account locked. Please contact support');
                    case 404:
                        throw new Error('No account found with these credentials');
                    case 422:
                        throw new Error('Invalid input data');
                    case 429:
                        const rateLimitTimeLeft = data?.timeLeft || null;
                        setError(data?.error || 'Too many login attempts. Please try again later');
                        return { success: false, error: data?.error || 'Too many login attempts. Please try again later', timeLeft: rateLimitTimeLeft };
                    case 500:
                    case 502:
                    case 503:
                        throw new Error('Server error. Please try again later');
                    default:
                        throw new Error(`Login failed (${response.status})`);
                }
            }
        }
        
        // Handle successful response with business logic
        if (data.status === "success" || data.success) {
            log('Login successful, processing response...');
            
            setUserData(data.user, data.account, data.token || data.userToken, data.refreshToken);
            await refreshBankAccounts();
            
            log('Login process completed successfully');
            return { 
                success: true, 
                user: data.user,
                requiresPhoneVerification: data.requiresPhoneVerification || false,
                needsUsername: data.needsUsername || false
            };
        } else if (data.status === "error" && data.error) {
            const errorMsg = data.error;
            log('Login failed with message', errorMsg);
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } else {
            const errorMsg = data.error || data.message || 'Login failed';
            log('Login failed with message', errorMsg);
            setError(errorMsg);
            return { success: false, error: errorMsg };
        }
    } catch (error) {
         
        let errorMsg = error.message || 'Network error. Please try again.';
        
        // Handle network errors
        if (error.message.includes('Failed to fetch')) {
            errorMsg = 'Cannot connect to server. Please check your internet connection and try again.';
        } else if (error.message.includes('CORS')) {
            errorMsg = 'Connection error. Please try again or contact support.';
        } else if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
            errorMsg = 'Network connection issue. Please check your internet and try again.';
        }
        
        // Set the error in state
        setError(errorMsg);
        
        // Log the error for debugging
        log('Login error set in state', { error: errorMsg, originalError: error.message });
        
        // Return the error to the component
        return { 
            success: false, 
            error: errorMsg,
            statusCode: error.status || (error.response ? error.response.status : null)
        };
    }
};
 
// Google login - ENHANCED VERSION
const googleLogin = async (accessToken, email) => {
  log('Google login attempt', { email });
  
  try {
    setError(null);
    
    // Log the request for debugging
    log('Sending Google login request', { email: email });
    
    const response = await api.post(API_CONFIG.AUTH.GOOGLE_LOGIN, {
      email: email,
      accessToken: accessToken
    });
    
    const data = response.data;
    
    // log('Google login response received', {
    //   status: response.status,
    //   ok: response.status >= 200 && response.status < 300,
    //   dataStatus: data.status,
    //   success: data.success,
    //   hasUser: !!data.user,
    //   hasToken: !!data.token,
    //   hasAccount: !!data.account,
    //   requiresPhoneVerification: data.requiresPhoneVerification,
    //   fullData: data // Log full data for debugging
    // });

    // console.log('Google login response data:', data); // Detailed console log

    // Check if response indicates success
    if (data.status === "success" || data.success) {
      // Validate that we have the required user data
      if (!data.user && !data.account) {
        const errorMsg = 'Incomplete user data received from server';
        log('Google login failed - incomplete data', { data });
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      log('Google login successful, processing response...');
      
      // Use unified function to set user data
      setUserData(data.user, data.account, data.token, data.refreshToken);
      await refreshBankAccounts();
      
      log('Google login process completed successfully');
      
      // Return the complete result including phone verification status
      return { 
        success: true, 
        user: data.user,
        requiresPhoneVerification: data.requiresPhoneVerification || false,
        message: data.message || 'Login successful'
      };
    } else {
      // Handle business logic errors from successful HTTP response
      const errorMsg = data.error || data.message || 'Google login failed';
      log('Google login failed with message', { error: errorMsg, data });
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    // Log full error details
    log('GOOGLE LOGIN ERROR DETAILS:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      } : null,
      request: error.request ? 'Request was made but no response received' : null,
      config: error.config ? {
        url: error.config.url,
        method: error.config.method,
        data: error.config.data
      } : null
    });

    log('Google login error caught', {
      errorName: error.name,
      errorMessage: error.message,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });

    let errorMsg = 'Network error. Please try again.';
    let statusCode = null;

    // Handle Axios error responses
    if (error.response) {
      // The request was made and the server responded with a status code
      statusCode = error.response.status;
      
      // Check if server returned a custom error message
      if (error.response.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response.data?.message) {
        errorMsg = error.response.data.message;
      } else {
        // Map HTTP status codes to user-friendly messages
        switch (error.response.status) {
          case 400:
            errorMsg = 'This email is associated with a regular account. Please use email and password to login.';
            break;
          case 401:
            errorMsg = 'Google authentication failed. Please try again or use a different account.';
            break;
          case 403:
            errorMsg = 'Access denied. Please contact support if this persists.';
            break;
          case 404:
            errorMsg = 'No account found with this Google email. Please sign up first.';
            break;
          case 409:
            errorMsg = 'An account with this email already exists. Please try logging in with password.';
            break;
          case 422:
            errorMsg = 'Invalid Google authentication data. Please try again.';
            break;
          case 429:
            errorMsg = 'Too many login attempts. Please try again later.';
            break;
          case 500:
          case 502:
          case 503:
            errorMsg = 'Server error. Please try again later or contact support.';
            break;
          default:
            errorMsg = `Login failed (${error.response.status}). Please try again.`;
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      errorMsg = 'Cannot connect to server. Please check your internet connection and try again.';
      log('No response received:', error.request);
    } else {
      // Something happened in setting up the request
      errorMsg = error.message || 'An error occurred during login. Please try again.';
    }

    // Handle network errors
    if (error.message?.includes('Network Error')) {
      errorMsg = 'Network connection issue. Please check your internet and try again.';
    } else if (error.code === 'ECONNABORTED') {
      errorMsg = 'Request timeout. Please check your connection and try again.';
    } else if (error.message?.includes('CORS')) {
      errorMsg = 'Connection error. Please try again or contact support.';
    }

    // Log the error that will be displayed to the user
    log('Google login error set in state', { 
      errorMsg, 
      originalError: error.message,
      statusCode 
    });

    setError(errorMsg);
    
    // Return comprehensive error object
    return { 
      success: false, 
      error: errorMsg,
      statusCode: statusCode,
      originalError: error.message
    };
  }
};
  // Refresh user data from server
  const refreshUserData = useCallback(async () => {
    try {
      log('Refreshing user data...');
      const response = await api.get(API_CONFIG.AUTH.IS_LOGGED_IN);
      if (response.data) {
        const { userData, accountData } = processIsLoggedInResponse(response.data);
        if (userData) {
          setUserData(userData, accountData);
          await refreshBankAccounts();
          log('User data refreshed successfully');
          return true;
        }
      }
      return false;
    } catch (error) {
      log('Error refreshing user data', error);
      return false;
    }
  }, [log, setUserData, processIsLoggedInResponse, refreshBankAccounts]);

  // Logout function - KEEP logout toast since it's not in the login form
  const logout = async () => {
    log('Logout initiated');
    
    try {
      const refreshToken = localStorage.getItem('_rt');
      await api.post(API_CONFIG.AUTH.LOGOUT, { refreshToken });
    } catch (error) {
      log('Backend logout error', error);
    } finally {
      clearAuthData();
      setCurrentUser(null);
      setBankAccounts([]);
      setUserProfileComplete(false);
      setError(null);
      
      showToast('Logged out successfully', 'success');
      log('Logout completed');
    }
  };

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Handle unauthorized events from api.js interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      log('Unauthorized event received - clearing auth');
      clearAuthData();
      setCurrentUser(null);
      setBankAccounts([]);
      setUserProfileComplete(false);
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        sessionStorage.setItem('returnUrl', window.location.pathname + window.location.search);
        window.location.href = '/login';
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('auth:unauthorized', handleUnauthorized);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('auth:unauthorized', handleUnauthorized);
      }
    };
  }, [clearAuthData, log]);

  // Initialize on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const value = {
    // State
    currentUser,
    currentAccountDetails,
    existingAccount,
    bankAccounts,
    userProfileComplete,
    loading,
    error,
    
    // Actions
    login,
    logout,
    googleLogin,
    clearError,
    refreshUserData,
    refreshBankAccounts,
    setUserData,
    
    // Utilities
    isAuthenticated: !!currentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};